import React, {useCallback, useEffect, useMemo, useRef, useState} from "react";
import Form from "../../../base/form";
import {MuiRFF, Yup} from '../../../form';
import DateUtils from "../../../../../core/services/date-utils";
import ValidateMessages from "../../../../../core/models/static/texts/vallidate-messages";
import CustomerAutocomplete from "../../../text-fields/autocomplete/customer-autocomplete";
import ToggleButtons from "../../../base/button/toggle_buttons";
import OperatorAutocomplete from "../../../text-fields/autocomplete/operator-autocomplete";
import {Button, Card, MenuItem, Paper, Unstable_Grid2 as Grid} from "@mui/material";
import PartAutocomplete from "../../../text-fields/autocomplete/part-autocomplete";
import {LookupAnalyticsDateOptionRanges, LookupAnalyticsDateOptionsValues} from "../dashboard";
import useIndustry from "../../../../hooks/use-industry";

export const reviewStatusValues = {
    all: 'all',
    reviewed: 'reviewed',
    notReviewed: 'notReviewed',
}

export const clickStatusValues = {
    all: 'all',
    clicked: 'clicked',
    notClicked: 'notClicked',
}

const LookupAnalyticsLogsViewSearchSection = ({filters, setFilters, showIndustry = false}) => {
    const {industries, cachedIndustry, fetchIndustry, fetchedIndustry} = useIndustry();
    const [dateOption, setDateOption] = useState();
    const minDate = useMemo(() => DateUtils.UTCNow().subtract(1, 'year'), []);

    /**@type {React.MutableRefObject<formApi>}*/
    const formApiRef = useRef();

    const schema = useMemo(() => Yup.object().shape({
        industryId: Yup.string().nullable(),
        keyword: Yup.string().nullable().trim(),
        clickStatus: Yup.string().nullable().default(clickStatusValues.all),
        reviewStatus: Yup.string().nullable().default(reviewStatusValues.all),
        part: Yup.mixed().nullable(),
        customer: Yup.mixed().nullable(),
        operator: Yup.mixed().nullable(),
        startDate: Yup.mixed()
            .validDate(ValidateMessages.incorrect)
            .disableFutureDate(ValidateMessages.maxDate('today'), 'day')
            .maxDate(Yup.ref('endDate'), ValidateMessages.lessThan("End Date"))
            .nullable()
        ,
        endDate: Yup.mixed()
            .validDate(ValidateMessages.incorrect)
            .disableFutureDate(ValidateMessages.maxDate('today'), 'day')
            .minDate(DateUtils.UTCNow().subtract(1, 'year'), ValidateMessages.greaterThan("1 year ago"))
            .minDate(Yup.ref("startDate"), ValidateMessages.greaterThan("Start Date"))
            .nullable(),
    }), []);

    const validate = useMemo(() => MuiRFF.makeValidate(schema), [schema]);
    const required = useMemo(() => MuiRFF.makeRequired(schema), [schema]);

    const initialValues = useMemo(() => ({
        ...(showIndustry ? {industryId: filters?.industryId ?? fetchedIndustry?.id ?? cachedIndustry?.id} : {}),
        keyword: filters?.keyword,
        clickStatus: filters?.clickStatus,
        reviewStatus: filters?.reviewStatus,
        customer: filters?.customer,
        part: filters?.part,
        operator: filters?.operator,
        startDate: filters?.startDate ? DateUtils.serverToUTC(filters?.startDate) : LookupAnalyticsDateOptionRanges.last2Weeks().startDate,
        endDate: filters?.endDate ? DateUtils.serverToUTC(filters?.endDate) : LookupAnalyticsDateOptionRanges.last2Weeks().endDate,
    }), [filters, cachedIndustry?.id, showIndustry, fetchedIndustry?.id]);

    /**
     * Automatically fetches the industry if there is only one industry available and it's not already fetched or selected
     */
    useEffect(() => {
        if (industries?.length === 1) {
            const industry = industries[0];
            void fetchIndustry({industryId: industry.id});
        }
    }, [fetchIndustry, industries]);

    /**
     * Sets the date option based on the filters
     */
    useEffect(() => {
        const period = {
            startDate: filters?.startDate ? DateUtils.serverToUTC(filters?.startDate) : LookupAnalyticsDateOptionRanges.last2Weeks().startDate,
            endDate: filters?.endDate ? DateUtils.serverToUTC(filters?.endDate) : LookupAnalyticsDateOptionRanges.last2Weeks().endDate,
        }
        const ranges = Object.entries(LookupAnalyticsDateOptionRanges).map(e => ({key: e[0], range: e[1]()}));
        const found = ranges.find(e => e.range.startDate.isSame(period.startDate, 'minute') && e.range.endDate.isSame(period.endDate, 'minute'));
        if (found) {
            setDateOption(LookupAnalyticsDateOptionsValues[found.key]);
        }
    }, [filters?.startDate, filters?.endDate])

    /**
     * Changes the query based on the added filters which triggers the search in responsible view
     * @param values
     */
    const onSubmit = (values) => {
        setFilters(prevState => ({
            ...prevState,
            ...(showIndustry ? {industryId: values.industryId} : {}),
            keyword: values.keyword,
            clickStatus: values.clickStatus,
            reviewStatus: values.reviewStatus,
            customer: values.customer,
            operator: values.operator,
            startDate: values.startDate ? DateUtils.UTCToServer(values.startDate).format(DateUtils.withoutOffsetFormat) : undefined,
            endDate: values.endDate ? DateUtils.UTCToServer(values.endDate).format(DateUtils.withoutOffsetFormat) : undefined,
            part: values.part,
        }))
    }

    /**
     * Changes the date input values based on the selected toggle button option
     */
    const changeDateByToggleButtons = useCallback((value) => {
        // refresh the same value if clicked again.
        if (!value) {
            value = dateOption;
        }
        setDateOption(value);
        let period = LookupAnalyticsDateOptionRanges[value]?.();
        if (!period)
            return;
        formApiRef.current?.change('startDate', period.startDate);
        formApiRef.current?.change('endDate', period.endDate);
    }, [dateOption])

    const predefinedDateOptions = useMemo(() => [
        {
            value: LookupAnalyticsDateOptionsValues.last24Hours,
            name: "Last 24 hours",
            disabled: false,
        },
        {
            value: LookupAnalyticsDateOptionsValues.last7Days,
            name: "Last 7 days",
            disabled: false,
        },
        {
            value: LookupAnalyticsDateOptionsValues.last2Weeks,
            name: "Last 2 weeks",
            disabled: false,
        },
        {
            value: LookupAnalyticsDateOptionsValues.last30Days,
            name: "Last 30 days",
            disabled: false,
        },
    ], [])

    const clickStatusOptions = useMemo(() => [
        {label: 'All', value: clickStatusValues.all},
        {label: 'Clicked', value: clickStatusValues.clicked},
        {label: 'Not Clicked', value: clickStatusValues.notClicked},
    ], []);

    const reviewStatusOptions = useMemo(() => [
        {label: 'All', value: reviewStatusValues.all},
        {label: 'Reviewed', value: reviewStatusValues.reviewed},
        {label: 'Not Reviewed', value: reviewStatusValues.notReviewed},
    ], []);

    return (
        <>
            <Card
                variant={'outlined'}
                sx={{
                    marginBlock: 2,
                    padding: 2,
                    borderColor: 'divider',
                    backgroundColor: 'background.default',
                    display: 'flex',
                    flexDirection: 'column',
                    gap: 2,
                }}
            >
                <Paper sx={{boxShadow: 'none', padding: 2, display: 'flex', flexDirection: 'column', gap: 2}}>
                    <Form
                        onSubmit={onSubmit}
                        validate={validate}
                        initialValues={initialValues}
                        render={({values, form}) => {
                            formApiRef.current = form;
                            return (
                                <>
                                    <Grid container spacing={2}>
                                        <Grid xs={12} sx={{display: 'flex', justifyContent: 'start'}}>
                                            <ToggleButtons
                                                value={dateOption}
                                                onChange={(_, v) => changeDateByToggleButtons(v)}
                                                options={predefinedDateOptions}
                                                size={'small'}
                                            />
                                        </Grid>
                                        <Grid xs={12} sm={6}>
                                            <MuiRFF.DateTimePicker
                                                name={'startDate'}
                                                label={"Start Date"}
                                                required={required.startDate}
                                                maxDate={values.endDate}
                                                format="YYYY-MM-DD HH:mm"
                                                disableFuture
                                                minDate={minDate}
                                                onChange={(e) => setDateOption(null) || form.change('startDate', e)}
                                                textFieldProps={{
                                                    size: 'small'
                                                }}
                                            />
                                        </Grid>
                                        <Grid xs={12} sm={6}>
                                            <MuiRFF.DateTimePicker
                                                name={'endDate'}
                                                label={"End Date"}
                                                required={required.endDate}
                                                minDate={values.startDate ?? minDate}
                                                format="YYYY-MM-DD HH:mm"
                                                disableFuture
                                                onChange={(e) => setDateOption(null) || form.change('endDate', e)}
                                                size={'small'}
                                                textFieldProps={{
                                                    size: 'small'
                                                }}
                                            />
                                        </Grid>
                                        {
                                            showIndustry && industries?.length > 1 &&
                                            <Grid xs={12}>
                                                <MuiRFF.TextField
                                                    select
                                                    name={'industryId'}
                                                    size={'small'}
                                                    label={'Industry'}
                                                    fullWidth
                                                    SelectProps={{renderValue: (value) => industries.find(e => e.id === value)?.title ?? ""}}
                                                >
                                                    {
                                                        industries.map(industry => (
                                                            <MenuItem key={industry.id} value={industry.id}>
                                                                {industry.title}
                                                            </MenuItem>
                                                        ))
                                                    }
                                                </MuiRFF.TextField>
                                            </Grid>
                                        }
                                        <Grid xs={12}>
                                            <MuiRFF.TextField
                                                size={'small'}
                                                name={'keyword'}
                                                label={"Keywords"}
                                                required={required.keyword}
                                            />
                                        </Grid>
                                        <Grid xs={12} sm={6} lg={4}>
                                            <CustomerAutocomplete
                                                form
                                                required={required.customer}
                                                name={'customer'}
                                                label={'Customer'}
                                                placeholder={'Search for customer...'}
                                                fullWidth
                                                textFieldProps={{
                                                    size: 'small'
                                                }}
                                            />
                                        </Grid>
                                        <Grid xs={12} sm={6} lg={4}>
                                            <OperatorAutocomplete
                                                form
                                                required={required.operator}
                                                name={'operator'}
                                                label={'Operator'}
                                                placeholder={'Search for operator...'}
                                                fullWidth
                                                textFieldProps={{
                                                    size: 'small'
                                                }}
                                            />
                                        </Grid>
                                        <Grid xs={12} lg={4}>
                                            <PartAutocomplete
                                                form
                                                industry={showIndustry ? values.industryId : filters.industryId}
                                                name={'part'}
                                                required={required.part}
                                                label={'Part'}
                                                placeholder={'search for part...'}
                                                fullWidth
                                                textFieldProps={{
                                                    size: 'small'
                                                }}
                                            />
                                        </Grid>
                                        <Grid xs={12} sm={6} lg>
                                            <MuiRFF.TextField
                                                select
                                                size={'small'}
                                                name={'clickStatus'}
                                                label={"Click Status"}
                                                required={required.clickStatus}
                                                variant={'outlined'}
                                            >
                                                {
                                                    clickStatusOptions.map((option) => (
                                                        <MenuItem key={option.value} value={option.value}>
                                                            {option.label}
                                                        </MenuItem>
                                                    ))
                                                }
                                            </MuiRFF.TextField>
                                        </Grid>
                                        <Grid xs={12} sm={6} lg>
                                            <MuiRFF.TextField
                                                select
                                                size={'small'}
                                                name={'reviewStatus'}
                                                label={"Review Status"}
                                                required={required.reviewStatus}
                                                variant={'outlined'}
                                            >
                                                {
                                                    reviewStatusOptions.map((option) => (
                                                        <MenuItem key={option.value} value={option.value}>
                                                            {option.label}
                                                        </MenuItem>
                                                    ))
                                                }
                                            </MuiRFF.TextField>
                                        </Grid>
                                        <Grid xs={12} lg={'auto'} sx={{display: 'flex', justifyContent: {xs: 'center', lg: 'end'}}}>
                                            <Button
                                                variant={'contained'}
                                                color={'primary'}
                                                type={'submit'}
                                                size={'medium'}
                                                sx={{paddingBlock: 0.5, paddingInline: 2, minWidth: 190, display: 'flex'}}
                                            >
                                                Search
                                            </Button>
                                        </Grid>
                                    </Grid>
                                </>
                            );
                        }}
                    />
                </Paper>
            </Card>
        </>
    );
}

export default LookupAnalyticsLogsViewSearchSection;
